// VRG-156 — the brand's saved-customization library.
//
// Two layers, per the design (logo reusable across products, full design is
// tag-shape-specific):
//   • logos   — an uploaded logo (image + name), reusable across ANY tag product.
//   • designs — a full customization (logo + placement + colour + finish) saved for
//               a SPECIFIC tag form, since placement depends on the tag's size/shape.
//
// Persistence is localStorage keyed by the company id (from `veraSpace`) so it's
// scoped to the brand. TODO(VRG-156): move this to the backend (a company-owned
// `tag_customizations` table) so saved logos/designs are shared across a brand's
// users + devices rather than living in one browser.

import { useCallback, useEffect, useState } from 'react'

import type { TagCustomization } from './components/CustomizeTagModal'
import type { TagFormFactor } from 'constants/tagCatalog'

export interface SavedLogo {
  id: string
  name: string
  dataUrl: string
  createdAt: number
}

export interface SavedDesign {
  id: string
  name: string
  /** The tag form this design was positioned for (placement is shape/size-specific). */
  form: TagFormFactor
  customization: TagCustomization
  createdAt: number
}

interface LibraryState {
  logos: SavedLogo[]
  designs: SavedDesign[]
}

const EMPTY: LibraryState = { logos: [], designs: [] }

const companyId = (): string => {
  try {
    const raw = localStorage.getItem('veraSpace')
    return (raw ? JSON.parse(raw)?.state?.companyId : '') || 'anon'
  } catch {
    return 'anon'
  }
}

const storageKey = () => `vera:tagCustomizations:${companyId()}`

const read = (): LibraryState => {
  try {
    const raw = localStorage.getItem(storageKey())
    if (!raw) return EMPTY
    const parsed = JSON.parse(raw)
    return { logos: parsed.logos ?? [], designs: parsed.designs ?? [] }
  } catch {
    return EMPTY
  }
}

// Math.random()/Date.now() are fine in the browser (this isn't a workflow script).
const uid = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`

export function useTagCustomizationLibrary() {
  const [state, setState] = useState<LibraryState>(EMPTY)

  useEffect(() => {
    setState(read())
  }, [])

  const persist = useCallback((next: LibraryState) => {
    setState(next)
    try {
      localStorage.setItem(storageKey(), JSON.stringify(next))
    } catch {
      /* quota / disabled storage — keep the in-memory copy for this session */
    }
  }, [])

  /** Save an uploaded logo to the brand library (dedupes identical images by dataUrl). */
  const saveLogo = useCallback(
    (dataUrl: string, name: string): SavedLogo => {
      const existing = state.logos.find(l => l.dataUrl === dataUrl)
      if (existing) return existing
      const logo: SavedLogo = { id: uid(), name: name || 'Logo', dataUrl, createdAt: Date.now() }
      persist({ ...state, logos: [logo, ...state.logos] })
      return logo
    },
    [state, persist],
  )

  const deleteLogo = useCallback(
    (id: string) => persist({ ...state, logos: state.logos.filter(l => l.id !== id) }),
    [state, persist],
  )

  /** Save a full design for a specific tag form. */
  const saveDesign = useCallback(
    (name: string, form: TagFormFactor, customization: TagCustomization): SavedDesign => {
      const design: SavedDesign = { id: uid(), name: name || 'Design', form, customization, createdAt: Date.now() }
      persist({ ...state, designs: [design, ...state.designs] })
      return design
    },
    [state, persist],
  )

  const deleteDesign = useCallback(
    (id: string) => persist({ ...state, designs: state.designs.filter(d => d.id !== id) }),
    [state, persist],
  )

  /** Designs saved for a given tag form (the only ones whose placement fits it). */
  const designsForForm = useCallback(
    (form: TagFormFactor | null) => (form ? state.designs.filter(d => d.form === form) : []),
    [state.designs],
  )

  return {
    logos: state.logos,
    designs: state.designs,
    designsForForm,
    saveLogo,
    deleteLogo,
    saveDesign,
    deleteDesign,
  }
}
